import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import type { PrescriptionTemplateDetail } from "@interfaces";

export interface TemplateDetail extends PrescriptionTemplateDetail {
  localId: string;
}

interface TemplateCreationState {
  templateDetails: TemplateDetail[];
  addDetail: (detail: Omit<TemplateDetail, "localId">) => void;
  removeDetail: (localId: string) => void;
  clearDetails: () => void;
}

const TemplateCreationContext = createContext<TemplateCreationState>({
  templateDetails: [],
  addDetail: () => {},
  removeDetail: () => {},
  clearDetails: () => {},
});

export function useTemplateCreationContext() {
  return useContext(TemplateCreationContext);
}

interface TemplateCreationProviderProps {
  children: React.ReactNode;
}

export function TemplateCreationProvider({ children }: TemplateCreationProviderProps) {
  const [templateDetails, setTemplateDetails] = useState<TemplateDetail[]>([]);

  const addDetail = useCallback((detail: Omit<TemplateDetail, "localId">) => {
    const localId = crypto.randomUUID();

    setTemplateDetails((prev) => [...prev, { ...detail, localId }]);
  }, []);

  const removeDetail = useCallback((localId: string) => {
    setTemplateDetails((prev) => prev.filter((d) => d.localId !== localId));
  }, []);

  const clearDetails = useCallback(() => {
    setTemplateDetails([]);
  }, []);

  const value = useMemo(
    () => ({
      templateDetails,
      addDetail,
      removeDetail,
      clearDetails,
    }),
    [templateDetails, addDetail, removeDetail, clearDetails],
  );

  return (
    <TemplateCreationContext.Provider value={value}>
      {children}
    </TemplateCreationContext.Provider>
  );
}
